import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Member = { id: string; member_name: string; member_role?: string | null; is_chair: number | boolean };
type Meeting = { id: string; meeting_no: string; meeting_on: string; agenda?: string | null; status: string };

type Committee = {
  id: string;
  committee_code: string;
  name: string;
  purpose?: string | null;
  status: string;
  record_version: number;
  members: Member[];
  meetings: Meeting[];
};

export default function GovernanceCommitteeDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.governance.manage') || user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-governance-committee', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Committee>(`/education/governance/committees/${id}`)).data,
  });
  const row = query.data;
  const active = row?.status === 'active';

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['education-governance-committee', id] });
    queryClient.invalidateQueries({ queryKey: ['education-governance-committees'] });
  };

  const setStatus = useMutation({
    mutationFn: (status: string) =>
      api.post(`/education/governance/committees/${id}/status`, {
        status,
        record_version: row?.record_version,
      }),
    onSuccess: () => {
      message.success(t('committeeStatusUpdated'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const addMember = useMutation({
    mutationFn: (values: { member_name: string; member_role?: string; is_chair?: boolean }) =>
      api.post(`/education/governance/committees/${id}/members`, values),
    onSuccess: () => {
      message.success(t('committeeMemberAdded'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const addMeeting = useMutation({
    mutationFn: (values: { meeting_on: dayjs.Dayjs; agenda?: string }) =>
      api.post<{ id: string }>(`/education/governance/committees/${id}/meetings`, {
        meeting_on: values.meeting_on.format('YYYY-MM-DD'),
        agenda: values.agenda,
      }),
    onSuccess: (res) => {
      message.success(t('committeeMeetingCreated'));
      invalidate();
      const meetingId = res.data?.id;
      if (meetingId) navigate(`/education/governance/meetings/${meetingId}`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={row?.committee_code || t('governanceCommittees')}
      description={t('governanceCommitteesIntro')}
      tags={row ? <Tag>{row.status}</Tag> : null}
      extra={
        <Button onClick={() => navigate('/education/governance/committees')}>{t('governanceCommittees')}</Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('governanceMinutesHint')} />
        <Card size="small" loading={query.isLoading}>
          {row ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('name')}>{row.name}</Descriptions.Item>
              <Descriptions.Item label={t('purpose')} span={2}>
                {row.purpose || '—'}
              </Descriptions.Item>
            </Descriptions>
          ) : null}
        </Card>

        {canManage ? (
          <Button
            danger={active}
            loading={setStatus.isPending}
            onClick={() => setStatus.mutate(active ? 'inactive' : 'active')}
          >
            {active ? t('deactivateCommittee') : t('activateCommittee')}
          </Button>
        ) : null}

        <Card size="small" title={t('members')}>
          {canManage && active ? (
            <Form
              layout="inline"
              style={{ marginBottom: 16 }}
              onFinish={(values) => addMember.mutate(values)}
            >
              <Form.Item name="member_name" rules={[{ required: true }]}>
                <Input placeholder={t('memberName')} />
              </Form.Item>
              <Form.Item name="member_role">
                <Input placeholder={t('memberRole')} />
              </Form.Item>
              <Form.Item name="is_chair" valuePropName="checked">
                <Checkbox>{t('chair')}</Checkbox>
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={addMember.isPending}>
                {t('addMember')}
              </Button>
            </Form>
          ) : null}
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={row?.members || []}
            columns={[
              { title: t('memberName'), dataIndex: 'member_name' },
              { title: t('memberRole'), dataIndex: 'member_role', render: (v) => v || '—' },
              {
                title: t('chair'),
                dataIndex: 'is_chair',
                width: 80,
                render: (v) => (v ? t('yes') : '—'),
              },
            ]}
          />
        </Card>

        <Card size="small" title={t('meetings')}>
          {canManage && active ? (
            <Form
              layout="vertical"
              style={{ maxWidth: 480, marginBottom: 16 }}
              initialValues={{ meeting_on: dayjs() }}
              onFinish={(values) => addMeeting.mutate(values)}
            >
              <Form.Item name="meeting_on" label={t('meetingOn')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
              <Form.Item name="agenda" label={t('agenda')}>
                <Input.TextArea rows={2} maxLength={1000} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={addMeeting.isPending}>
                {t('scheduleMeeting')}
              </Button>
            </Form>
          ) : null}
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={row?.meetings || []}
            onRow={(m) => ({
              onClick: () => navigate(`/education/governance/meetings/${m.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('meetingNo'), dataIndex: 'meeting_no', width: 150 },
              { title: t('meetingOn'), dataIndex: 'meeting_on', width: 120 },
              { title: t('agenda'), dataIndex: 'agenda', ellipsis: true },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 110,
                render: (v: string) => <Tag>{v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
